import './Blacklist.sass';

import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Button, Header, Placeholder, ScrollView } from '$uikit';
import { Loader, UserCell } from '$shared/components';
import { useTranslator } from '$hooks';
import { Api } from '$api';
import { toastActions } from '$store/toast';
import { mainActions } from '$store/main';
import { blacklistActions } from '$store/blacklist';

export const Blacklist: FC = () => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const [userIds, setUserIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [nextFrom, setNextFrom] = useState<number | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const loadBlacklist = useCallback(
    (lastId?: number) => {
      if (lastId) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      const params: Record<string, any> = { size: 20 };
      if (lastId) params.lastId = lastId;

      Api.get<any, any>('/blacklist', { params })
        .then((resp) => {
          const users = resp.users || {};
          dispatch(mainActions.setUsers(users));

          const ids = Object.keys(users).map(Number);

          setUserIds((prev) => [...prev, ...ids]);

          setNextFrom(resp.nextFrom > 0 ? resp.nextFrom : null);
          setIsLoading(false);
          setIsLoadingMore(false);
        })
        .catch((e) => {
          dispatch(toastActions.setToastFail(e.message));
          setIsLoading(false);
          setIsLoadingMore(false);
        });
    },
    [],
  );

  useEffect(() => {
    loadBlacklist();
  }, []);

  const handleLoadMore = useCallback(() => {
    if (nextFrom !== null && !isLoadingMore) {
      loadBlacklist(nextFrom);
    }
  }, [nextFrom, isLoadingMore]);

  const handleUnblock = useCallback(
    (userId: number) => {
      dispatch(blacklistActions.unban(userId));
      setUserIds((prev) => prev.filter((id) => id !== userId));
    },
    [],
  );

  return (
    <>
      <Header showBackButton>{t('blacklist_title')}</Header>
      <ScrollView
        canLoadMore={!isLoading && !isLoadingMore && nextFrom !== null}
        onLoadMore={handleLoadMore}
        isLoadingMore={isLoadingMore}
      >
        <div className="Blacklist">
          {isLoading ? (
            <Loader isWrapped />
          ) : userIds.length === 0 ? (
            <Placeholder>{t('blacklist_empty')}</Placeholder>
          ) : (
            userIds.map((userId) => (
              <UserCell
                key={userId}
                objectId={userId}
                isFollowBtnShown={false}
                after={
                  <div className="UserCell__follow">
                    <Button
                      size="small"
                      mode="secondary"
                      onClick={() => handleUnblock(userId)}
                    >
                      {t('blacklist_unblock')}
                    </Button>
                  </div>
                }
              />
            ))
          )}
        </div>
      </ScrollView>
    </>
  );
};
